import { type ComponentPropsWithoutRef, useMemo } from 'react';

import { RadioGroup, RadioGroupItem } from '@/components/shadcn/ui/radio-group';
import { Separator } from '@/components/shadcn/ui/separator';
import { cn } from '@/lib/cn';

import type { SortPopoverOptions } from './types';

type Props = {
  titles?: {
    sort?: string;
    order?: string;
  };
  options: SortPopoverOptions[];
  sortValue: string;
  orderValue: string;
  onChangeSort: (props: { sortValue: string; orderValue?: string }) => void;
};

export const SortPopoverContent = ({
  titles = {
    sort: 'Sort by',
    order: 'Order by',
  },
  options,
  onChangeSort,
  sortValue,
  orderValue,
}: Props) => {
  const sortOptions = options.map((v) => v.sortOption);

  const orderOptions = useMemo(() => {
    const currentOption = options.find(
      (option) => option.sortOption.value === sortValue,
    );
    return currentOption?.orderOptions ?? [];
  }, [options, sortValue]);

  return (
    <div className="w-48">
      <span className="text-xs text-muted-foreground px-2">{titles.sort}</span>
      <div className="h-2" />
      <RadioGroup
        className="gap-1"
        value={sortValue}
        onValueChange={(newSortBy) => {
          onChangeSort({ sortValue: newSortBy, orderValue });
        }}
      >
        {sortOptions.map((option) => (
          <RadioGroupItemLabel
            key={option.value}
            htmlFor={`sort-${option.value}`}
            value={option.value}
            radioId={`sort-${option.value}`}
          >
            {option.label}
          </RadioGroupItemLabel>
        ))}
      </RadioGroup>

      {orderOptions.length > 0 && (
        <>
          <div className="h-2" />
          <Separator />
          <div className="h-3" />
          <span className="text-xs text-muted-foreground px-2">
            {titles.order}
          </span>
          <div className="h-2" />
          <RadioGroup
            className="gap-1"
            value={orderValue}
            onValueChange={(newOrder) => {
              onChangeSort({
                sortValue,
                orderValue: newOrder,
              });
            }}
          >
            {orderOptions.map((v) => (
              <RadioGroupItemLabel
                key={v.value}
                htmlFor={`order-${v.value}`}
                value={v.value}
                radioId={`order-${v.value}`}
              >
                {v.label}
              </RadioGroupItemLabel>
            ))}
          </RadioGroup>
        </>
      )}
    </div>
  );
};

const RadioGroupItemLabel = ({
  value,
  radioId,
  children,
  className,
  ...rest
}: ComponentPropsWithoutRef<'label'> & { value: string; radioId: string }) => {
  return (
    <label
      {...rest}
      className={cn(
        'cursor-pointer flex items-center gap-2 px-2 py-1 hover:bg-accent transition-colors duration-150 rounded-md',
        className,
      )}
    >
      <RadioGroupItem value={value} id={radioId} />
      <span className="text-sm">{children}</span>
    </label>
  );
};
